/**
 * Turns an `EnquiryRecord` — what `enquiriesService` holds — into the two
 * shapes the screens render: the list row and the detail record.
 *
 * The mirror of `clientMapping` in the clients feature: dates written the way
 * the design writes them, a name joined out of two fields, an age worked out
 * from a date of birth. Kept as its own module rather than folded into the
 * service, the same reasoning as the client version — none of it is about
 * where the data came from.
 */

import type {
  Enquiry,
  EnquiryDetail,
  EnquiryMode,
  EnquiryRecord,
  EnquiryStatus,
  EnquiryStatusCode,
} from "./types/enquiries.types";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "1 Jul 2026" — the table's date column. */
export const formatShortDate = (value: string | null): string => {
  const date = parseDate(value);
  if (!date) return "-";
  return `${date.getDate()} ${MONTHS[date.getMonth()].slice(
    0,
    3
  )} ${date.getFullYear()}`;
};

/** "26 March 2002" — the detail screen writes the month out in full. */
export const formatLongDate = (value: string | null): string => {
  const date = parseDate(value);
  if (!date) return "-";
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

/** Whole years since the date of birth. `null` when there is no date to count from. */
export const ageOn = (
  dateOfBirth: string | null,
  today: Date = new Date()
): number | null => {
  const born = parseDate(dateOfBirth);
  if (!born) return null;

  let age = today.getFullYear() - born.getFullYear();
  const monthDiff = today.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < born.getDate())) {
    age -= 1;
  }
  return age < 0 ? null : age;
};

const plural = (count: number, unit: string) =>
  `${count} ${unit}${count === 1 ? "" : "s"}`;

/** Midnight, so two dates compare on the day rather than the moment. */
const atMidnight = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

/**
 * How long ago the enquiry was raised, for the Days ago column — "Today",
 * "1 day", "5 days". Whole days, not a duration rounded up, so an enquiry
 * raised an hour ago reads "Today" rather than "1 day".
 */
export const daysAgoOf = (
  createdAt: string | null,
  today: Date = new Date()
): string => {
  const created = parseDate(createdAt);
  if (!created) return "-";

  const from = atMidnight(created);
  const to = atMidnight(today);
  if (to <= from) return "Today";

  return plural(Math.round((to.getTime() - from.getTime()) / MS_PER_DAY), "day");
};

/**
 * The same column written from a count the server already did — "Today",
 * "1 day", "5 days". Anything negative is a record raised in the future by a
 * clock that disagrees, which still reads as today rather than as "-1 days".
 */
export const daysAgoLabel = (days: number): string =>
  days <= 0 ? "Today" : plural(days, "day");

/**
 * The backend's stage constants, in the app's own vocabulary. Written out
 * rather than derived from the constants: `JUST_ADDED` is shown as "Recently
 * Added" and no rule turns one into the other, the labels are a closed union
 * the status pill is styled from, and a constant that stopped matching should
 * fall to the default below rather than quietly become a stage nothing can
 * render.
 */
const STATUS_BY_CODE: Record<EnquiryStatusCode, EnquiryStatus> = {
  JUST_ADDED: "Recently Added",
  INTERESTED: "Interested",
  NOT_INTERESTED: "Not interested",
  PAYMENT_SENT: "Payment sent",
  CONVERTED: "Converted",
};

/**
 * A stage the app knows, from whatever the API sent. An unfamiliar constant
 * reads as "Recently Added" — the opening stage, and the honest answer for an
 * enquiry the app cannot tell has been worked.
 */
export const toEnquiryStatus = (code: string): EnquiryStatus =>
  STATUS_BY_CODE[(code ?? "").trim().toUpperCase() as EnquiryStatusCode] ??
  "Recently Added";

/**
 * And back again, for the call that moves a stage: the word a screen shows
 * into the constant `PATCH .../update/` takes — "Not interested" as
 * `NOT_INTERESTED`.
 *
 * Read backwards off the same table rather than written out a second time, so
 * the pair cannot drift. A label with no constant of its own cannot arise —
 * `EnquiryStatus` is closed, and every one of its four is in the table above —
 * but if one ever did, it goes up as its own name upper-cased: something the
 * backend can reject clearly rather than a stage silently sent as another.
 */
export const toEnquiryStatusCode = (status: EnquiryStatus): string => {
  const match = (
    Object.entries(STATUS_BY_CODE) as [EnquiryStatusCode, EnquiryStatus][]
  ).find(([, label]) => label === status);
  return match?.[0] ?? status.trim().toUpperCase().replace(/\s+/g, "_");
};

/**
 * How the enquiry arrived: the backend's `InquirySource` constants, under the
 * labels that enum gives them. One entry per constant, and the labels are its
 * own — the app does not rename `CALL` to anything of its own, so the two
 * cannot come to say different words for the same source.
 *
 * `toEnquirySource` reads this backwards, which is why it is one-to-one.
 */
const MODE_BY_SOURCE: Record<string, EnquiryMode> = {
  SOCIAL_MEDIA: "Social media",
  EMAIL: "Email",
  CALL: "Call",
  FORM: "Form",
};

/** A source the app knows. Anything else is "Other" — see `EnquiryMode`. */
export const toEnquiryMode = (code: string): EnquiryMode =>
  MODE_BY_SOURCE[(code ?? "").trim().toUpperCase()] ?? "Other";

/**
 * And back again, for the create and update calls: what the Enquiry from
 * dropdown holds into the constant the endpoint takes — "Social media" as
 * `SOCIAL_MEDIA`. Kept beside its opposite rather than in `enquiryPayload`, so the
 * pair cannot drift.
 *
 * A mode with no constant of its own — only "Other", which the dropdown never
 * offers — goes up as its own name upper-cased: the honest thing to send, and
 * something the backend can reject clearly.
 */
export const toEnquirySource = (mode: string): string => {
  const label = (mode ?? "").trim();
  const match = Object.entries(MODE_BY_SOURCE).find(
    ([, name]) => name === label
  );
  return match?.[0] ?? label.toUpperCase().replace(/\s+/g, "_");
};

/** Both name fields joined, and never blank — a row with no name is unclickable. */
export const fullNameOf = (
  record: Pick<EnquiryRecord, "firstName" | "lastName">
): string => `${record.firstName} ${record.lastName}`.trim() || "Unnamed enquiry";

/**
 * Street, city, state and PIN code, skipping whichever came back empty.
 * Ordered outward from the door, which is how an Indian address is written.
 */
export const addressOf = (record: EnquiryRecord): string =>
  [record.fullAddress, record.city, record.state, record.postalCode]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(", ") || "-";

/** An enquiry as the list table renders one. */
export const toEnquiryRow = (record: EnquiryRecord): Enquiry => ({
  id: record.id,
  name: fullNameOf(record),
  goals: record.goals,
  mode: record.mode,
  requestedOn: formatShortDate(record.createdAt),
  // The server's count when there is one; an enquiry made in this sitting has
  // never been near it, so that one is counted here.
  daysAgo:
    record.daysAgo === undefined
      ? daysAgoOf(record.createdAt)
      : daysAgoLabel(record.daysAgo),
  status: record.status,
  statusLabel: record.statusLabel,
  payment: record.payment,
});

/** An enquiry as the detail screen renders one. */
export const toEnquiryDetail = (record: EnquiryRecord): EnquiryDetail => {
  const row = toEnquiryRow(record);
  const age = ageOn(record.dateOfBirth);

  return {
    ...row,
    salutation: record.title ? `${record.title}.` : "",
    gender: record.gender || "-",
    dob: formatLongDate(record.dateOfBirth),
    age,
    lifestyle: record.lifestyle || "-",
    profession: record.profession || "-",
    requestedVia: record.mode,
    phone: record.phone || "-",
    whatsapp: record.whatsapp || "-",
    email: record.email || "-",
    address: addressOf(record),
    payment: record.payment && {
      ...record.payment,
      requestedOn: formatLongDate(record.payment.requestedAt),
    },
  };
};
